<template>
    <div :class="[$style.container, nativeBannersEnabled && $style.static]">
        <div :class="[$style.front, isAdVisible && (nativeBannersEnabled ? $style.covered : $style.hideUnder)]">
            <slot name="front" />
        </div>
        <div :class="[$style.back, isAdVisible && $style.visible]">
            <AdContainer
                v-if="adMounted"
                :class="$style.mobile"
                :placement="adMobilePlacement"
                @update:visible="onAdVisibilityChanged"
            />
            <AdContainer
                v-if="adMounted"
                :class="$style.tablet"
                :placement="adTabletPlacement"
                @update:visible="onAdVisibilityChanged"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import AdContainer from '@/components/AdContainer/AdContainer.vue'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAdsStore } from '@/store/ads-store'

const props = withDefaults(
    defineProps<{
        frontDurationMs?: number
        backDurationMs?: number
        widgetMode?: boolean
    }>(),
    {
        frontDurationMs: 15000,
        backDurationMs: 60000,
    },
)

const { nativeBannersEnabled } = storeToRefs(useAdsStore())
const { isAdDisabled } = storeToRefs(useAdDisabledStore())

const REFETCH_FAILED_AD_MS = 60_000

const isAdVisible = ref(false)
const adMounted = ref(true)
const retryTimer = ref<ReturnType<typeof setTimeout> | null>(null)

const adMobilePlacement = computed(() => (props.widgetMode ? 'widget_sticky_mobile' : 'sticky_mobile'))
const adTabletPlacement = computed(() => (props.widgetMode ? 'widget_sticky_tablet' : 'sticky_tablet'))

function onAdVisibilityChanged(isVisible: boolean) {
    if (isVisible) {
        isAdVisible.value = true
        return
    }
    isAdVisible.value = false
    adMounted.value = false
    clearTimeout(retryTimer.value!)
    retryTimer.value = setTimeout(() => {
        adMounted.value = true
    }, REFETCH_FAILED_AD_MS)
}

watch(isAdDisabled, (disabled) => {
    clearTimeout(retryTimer.value!)
    isAdVisible.value = false
    adMounted.value = !disabled
})

onBeforeUnmount(() => {
    clearTimeout(retryTimer.value!)
})
</script>

<style module>
.container {
    background-color: var(--black);
    position: relative;
    height: 100%;
    overflow: hidden;
}

.front,
.back {
    position: absolute;
    inset: 0;
    transition: transform 0.6s ease;
}

.hideUnder {
    transform: translateY(-100%);
}

.back {
    transform: translateY(100%);
}

.back.visible {
    transform: translateY(0);
}

.static .front,
.static .back {
    transform: none;
    transition: none;
}

.covered {
    visibility: hidden;
}

@media (prefers-color-scheme: light) {
    .container {
        background-color: var(--white);
    }
}

.tablet {
    display: none;
}

@media (--tablet-plus) {
    .tablet {
        display: block;
    }

    .mobile {
        display: none;
    }
}
</style>
